import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { A11yModule } from '@angular/cdk/a11y';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { OverlayModule } from '@angular/cdk/overlay';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MatMomentDateModule,
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter
} from '@angular/material-moment-adapter';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { DateAdapter, MatNativeDateModule, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule, MAT_TABS_CONFIG } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AverageDailyDeskUsageComponent } from '@shared/components/average-daily-desk-usage/average-daily-desk-usage.component';
import { AverageOrganisationsUtilizationComponent } from '@shared/components/average-organisations-utilization/average-organisations-utilization.component';
import { BarChartComponent } from '@shared/components/bar-chart/bar-chart.component';
import { CapacityUtilizationByDepartmentsOptionsComponent } from '@shared/components/capacity-utilization-by-departments-options/capacity-utilization-by-departments-options.component';
import { CapacityUtilizationByDepartmentsComponent } from '@shared/components/capacity-utilization-by-departments/capacity-utilization-by-departments.component';
import { CapacityUtilizationDepartmentsComparisonComponent } from '@shared/components/capacity-utilization-departments-comparison/capacity-utilization-departments-comparison.component';
import { DownloadFileSnackBarComponent } from '@shared/components/download-file-snack-bar/download-file-snack-bar.component';
import { HourlyWorkspaceUtilizationComponent } from '@shared/components/hourly-workspace-utilization/hourly-workspace-utilization.component';
import { HybridProfileDistributionComponent } from '@shared/components/hybrid-profile-distribution/hybrid-profile-distribution.component';
import { LineChartComponent } from '@shared/components/line-chart/line-chart.component';
import { MixLineBarChartComponent } from '@shared/components/mix-line-bar-chart/mix-line-bar-chart.component';
import { PieChartComponent } from '@shared/components/pie-chart/pie-chart.component';
import { QrcodeScannerComponent } from '@shared/components/qrcode-scanner/qrcode-scanner.component';
import { ReportPdfTemplateComponent } from '@shared/components/report-pdf-template/report-pdf-template.component';
import { UtilizationTimeSlotComponent } from '@shared/components/utilization-time-slot/utilization-time-slot.component';
import { WorkspaceUtilizationSpaceTypeComponent } from '@shared/components/workspace-utilization-space-type/workspace-utilization-space-type.component';
import { CustomDateAdapter } from '@shared/custom-date-adapter';
import { AutofocusDirective } from '@shared/directive/autofocus.directive';
import { OverlaySpinnerDirective } from '@shared/directive/overlay-spinner.directive';
import { FirstLetterToUpperCasePipe } from '@shared/pipe/first-letter-to-upper-case.pipe';
import { TextMaskModule } from 'angular2-text-mask';
import { icons, LucideAngularModule } from 'lucide-angular';
import { AutoSizeInputModule } from 'ngx-autosize-input';
import { NgxEchartsModule } from 'ngx-echarts';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ConfirmationModalComponent } from './components/confirmation-modal/confirmation-modal.component';
import { CounterComponent } from './components/counter/counter.component';
import { DataSetComponent } from './components/data-set/data-set.component';
import { EmptyStateComponent } from './components/empty-state/empty-state.component';
import { ErrorPageComponent } from './components/error-page/error-page.component';
import { FormGroupComponent } from './components/form-group/form-group.component';
import { LayoutComponent } from './components/layout/layout.component';
import { NoDataComponent } from './components/no-data/no-data.component';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { ProgressComponent } from './components/progress/progress.component';
import { ReportPdfPageComponent } from './components/report-pdf-page/report-pdf-page.component';
import { ReportPdfSectionComponent } from './components/report-pdf-section/report-pdf-section.component';
import { SidenavFooterComponent } from './components/sidenav-footer/sidenav-footer.component';
import { SnackBarWrapperComponent } from './components/snack-bar-wrapper/snack-bar-wrapper.component';
import { TextWithInfoTooltipComponent } from './components/text-with-info-tooltip/text-with-info-tooltip.component';
import { DragAndDropDirective } from './directive/drag-and-drop.directive';
import { NumbersOnlyDirective } from './directive/numbers-only.directive';
import { TrimCopyDirective } from './directive/trim-copy.directive';
import { TrimValueDirective } from './directive/trim-value.directive';
import { LastWordPipe } from './pipe/last-word.pipe';
import { NumberFormatPipe } from './pipe/number-format.pipe';
import { WithoutLastWordPipe } from './pipe/without-last-word.pipe';

const materialModules = [
  MatBadgeModule,
  MatButtonModule,
  MatSelectModule,
  MatTabsModule,
  MatInputModule,
  OverlayModule,
  MatProgressSpinnerModule,
  MatChipsModule,
  MatCardModule,
  MatCheckboxModule,
  MatListModule,
  MatMenuModule,
  MatIconModule,
  MatTooltipModule,
  MatSnackBarModule,
  MatSlideToggleModule,
  MatDividerModule,
  MatSidenavModule,
  MatToolbarModule,
  MatDatepickerModule,
  MatNativeDateModule,
  MatTableModule,
  MatSortModule,
  MatDialogModule,
  MatExpansionModule,
  DragDropModule,
  MatPaginatorModule,
  MatIconModule,
  MatButtonToggleModule,
  MatRadioModule,
  MatFormFieldModule,
  MatAutocompleteModule,
  MatGridListModule,
  NgxMatSelectSearchModule,
  LucideAngularModule.pick(icons),
  MatMomentDateModule,
  NgxEchartsModule.forRoot({
    echarts: () => import('echarts')
  }),
  FlexLayoutModule,
  MatProgressBarModule,
  NgxSkeletonLoaderModule,
  MatProgressBarModule,
  TextMaskModule
];

const directives = [AutofocusDirective, OverlaySpinnerDirective, TrimValueDirective];

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    materialModules,
    FlexLayoutModule,
    A11yModule,
    ScrollingModule,
    AutoSizeInputModule,
    NgxSliderModule
  ],
  exports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    materialModules,
    FlexLayoutModule,
    A11yModule,
    ScrollingModule,
    FirstLetterToUpperCasePipe,
    ...directives,
    DragAndDropDirective,
    QrcodeScannerComponent,
    PageNotFoundComponent,
    FormGroupComponent,
    DataSetComponent,
    TextWithInfoTooltipComponent,
    EmptyStateComponent,
    CounterComponent,
    AverageDailyDeskUsageComponent,
    LineChartComponent,
    ReportPdfTemplateComponent,
    ConfirmationModalComponent,
    UtilizationTimeSlotComponent,
    WorkspaceUtilizationSpaceTypeComponent,
    HourlyWorkspaceUtilizationComponent,
    BarChartComponent,
    HybridProfileDistributionComponent,
    PieChartComponent,
    CapacityUtilizationDepartmentsComparisonComponent,
    MixLineBarChartComponent,
    CapacityUtilizationByDepartmentsComponent,
    CapacityUtilizationByDepartmentsOptionsComponent,
    AverageOrganisationsUtilizationComponent,
    NgxSliderModule,
    NumbersOnlyDirective,
    SidenavFooterComponent,
    LayoutComponent,
    NoDataComponent,
    DownloadFileSnackBarComponent,
    NumberFormatPipe,
    TrimCopyDirective,
    ErrorPageComponent
  ],
  declarations: [
    FirstLetterToUpperCasePipe,
    ...directives,
    DragAndDropDirective,
    QrcodeScannerComponent,
    SnackBarWrapperComponent,
    PageNotFoundComponent,
    FormGroupComponent,
    DataSetComponent,
    TextWithInfoTooltipComponent,
    EmptyStateComponent,
    CounterComponent,
    AverageDailyDeskUsageComponent,
    LineChartComponent,
    ReportPdfTemplateComponent,
    ConfirmationModalComponent,
    UtilizationTimeSlotComponent,
    WorkspaceUtilizationSpaceTypeComponent,
    HourlyWorkspaceUtilizationComponent,
    BarChartComponent,
    HybridProfileDistributionComponent,
    PieChartComponent,
    CapacityUtilizationDepartmentsComparisonComponent,
    MixLineBarChartComponent,
    CapacityUtilizationByDepartmentsComponent,
    CapacityUtilizationByDepartmentsOptionsComponent,
    AverageOrganisationsUtilizationComponent,
    NumbersOnlyDirective,
    SidenavFooterComponent,
    LayoutComponent,
    ProgressComponent,
    NoDataComponent,
    DownloadFileSnackBarComponent,
    ReportPdfPageComponent,
    ReportPdfSectionComponent,
    NumberFormatPipe,
    LastWordPipe,
    WithoutLastWordPipe,
    TrimCopyDirective,
    ErrorPageComponent
  ],
  providers: [
    { provide: MAT_TABS_CONFIG, useValue: { animationDuration: '0ms' } },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    {
      provide: MAT_DATE_FORMATS,
      deps: [TranslateService],
      useValue: {
        parse: {
          dateInput: 'DD/MM/YYYY'
        },
        display: {
          dateInput: 'DD/MM/YYYY',
          monthYearLabel: 'MMM YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'MMMM YYYY'
        }
      }
    },
    { provide: MomentDateAdapter, useClass: CustomDateAdapter },
    { provide: DateAdapter, useClass: CustomDateAdapter },
    DatePipe,
    DecimalPipe,
    NumberFormatPipe
  ]
})
export class SharedModule {}
